/**
 * Setup step — shared UI helpers (Feb 2026 refactor).
 *
 * Pulled out of the ~1080-line `ProfileSetup.jsx` so each `Step*.jsx` can
 * consume the same pill/prompt/lifestyle building blocks without importing
 * the parent page.
 *
 * Contract stays identical to the original inline components: every
 * `data-testid` is preserved so Playwright suites keep passing.
 */
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';

/** Single-select rounded-pill row. */
export function PillSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-sm text-zinc-600 mb-1.5 block">{label}</Label>
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(value === o ? '' : o)}
            data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className={
              'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ' +
              (value === o
                ? 'brand-gradient text-white border-transparent'
                : 'bg-white text-[#4B4B52] border-[var(--hairline-strong)] hover:bg-black/[0.02]')
            }
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Multi-select pill picker. `value` is an array of selected options.
 *  Feb 2026 · v2.2.0 — used by StepAboutYou.jsx for Ethnicity (accepts
 *  multiple identities). Renders identically to PillSelect for visual
 *  parity; the caller toggles items in/out of an array. */
export function PillMulti({ label, subtitle, values, onChange, options, testId, max = null }) {
  const set = new Set(Array.isArray(values) ? values : []);
  const toggle = (o) => {
    if (set.has(o)) {
      onChange(Array.from(set).filter((x) => x !== o));
    } else {
      if (max && set.size >= max) { toast.error(`Max ${max} selection${max === 1 ? '' : 's'}`); return; }
      onChange([...Array.from(set), o]);
    }
  };
  return (
    <div>
      <Label className="text-sm text-zinc-600 mb-1 block">{label}</Label>
      {subtitle && <p className="text-[11px] text-zinc-500 mb-2">{subtitle}</p>}
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {options.map((o) => {
          const active = set.has(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className={
                'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ' +
                (active
                  ? 'brand-gradient text-white border-transparent'
                  : 'bg-white text-[#4B4B52] border-[var(--hairline-strong)] hover:bg-black/[0.02]')
              }
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Prompt builder — up to 3, add/remove/edit each answer.
 *  Feb 2026 · v2.3.0 — when `categories` is provided (from
 *  GET /api/prompts/categories), renders 4 category sections instead of
 *  a flat 26-chip cloud. Falls back to the flat picker if the endpoint
 *  hasn't loaded yet (older backend). */
export function PromptsBuilder({ available, categories = [], prompts, onChange }) {
  const selected = Array.isArray(prompts) ? prompts : [];
  const add = (key) => {
    if (selected.find((p) => p.key === key)) return;
    if (selected.length >= 3) { toast.error('Max 3 prompts'); return; }
    onChange([...selected, { key, answer: '' }]);
  };
  const remove = (key) => onChange(selected.filter((p) => p.key !== key));
  const update = (key, answer) => onChange(selected.map((p) => (p.key === key ? { ...p, answer } : p)));

  const isSelected = (key) => selected.find((x) => x.key === key);
  const useCategorized = categories && categories.length > 0;

  return (
    <div id="section-prompts">
      <div className="mb-2">
        <h3 className="text-[16px] font-bold tracking-tight text-[#0F0F10]">
          Make It Personal <span className="ml-2 text-[11px] font-semibold text-[#B91C1C] align-middle">{selected.length}/3</span>
        </h3>
        <p className="text-[12px] text-[#8A8A93] mt-0.5 leading-relaxed">
          Pick up to 3 prompts that give matches something to connect with — and something to talk about.
        </p>
      </div>

      <div className="space-y-2.5 mb-3" data-testid="setup-prompts-list">
        {selected.map((p) => {
          const meta = available.find((x) => x.key === p.key);
          return (
            <div key={p.key} className="bg-black/[0.03] border border-black/[0.06] rounded-xl p-3" data-testid={`setup-prompt-${p.key}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-[12px] font-semibold text-[#0F0F10] leading-snug">{meta?.label || p.key}</p>
                <button type="button" onClick={() => remove(p.key)}
                  className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#8A8A93]"
                  data-testid={`setup-remove-prompt-${p.key}`}>
                  <X size={13} />
                </button>
              </div>
              <Textarea value={p.answer || ''} maxLength={160}
                onChange={(e) => update(p.key, e.target.value)}
                placeholder="Your answer..."
                data-testid={`setup-prompt-answer-${p.key}`}
                className="rounded-lg bg-white border-black/[0.08] text-[#0F0F10] px-3 py-2 min-h-[56px] resize-none text-[13px]" />
              <p className="text-[10px] text-zinc-500 mt-1 text-right">{(p.answer || '').length}/160</p>
            </div>
          );
        })}
      </div>

      {selected.length < 3 && (
        useCategorized ? (
          <div className="space-y-4" data-testid="setup-prompts-picker">
            {categories.map((cat) => {
              const available_in_cat = cat.items.filter((p) => !isSelected(p.key));
              if (available_in_cat.length === 0) return null;
              return (
                <div key={cat.key} data-testid={`setup-prompts-cat-${cat.key}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[13px]" aria-hidden="true">{cat.icon}</span>
                    <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#8A8A93]">
                      {cat.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {available_in_cat.map((p) => (
                      <button key={p.key} type="button" onClick={() => add(p.key)}
                        data-testid={`setup-add-prompt-${p.key}`}
                        className="px-3 py-1.5 rounded-full text-[11px] font-semibold border bg-white text-[#4B4B52] border-black/[0.08] hover:bg-black/[0.02] hover:border-[#B91C1C]/40 transition-all">
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          available.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="setup-prompts-picker">
              {available
                .filter((p) => !isSelected(p.key))
                .map((p) => (
                  <button key={p.key} type="button" onClick={() => add(p.key)}
                    data-testid={`setup-add-prompt-${p.key}`}
                    className="px-3 py-1.5 rounded-full text-[11px] font-semibold border bg-white text-[#4B4B52] border-black/[0.08] hover:bg-black/[0.02] hover:border-[#B91C1C]/40 transition-all">
                    {p.label}
                  </button>
                ))}
            </div>
          )
        )
      )}
    </div>
  );
}

/**
 * Lifestyle field pill grid — organized into 3 semantic sections that mirror
 * the Discover filter drawer + user's 5-section model:
 *   • Relationship     — kids, marital, monogamy, relocate
 *   • Lifestyle        — drinking, smoking, cannabis, exercise, pets
 *   • Dating           — meeting_readiness, date_type_prefs
 * Backend persists at `profile.lifestyle_answers.<key>` (except the two
 * Dating fields, which persist on `profile.meeting_prefs.<key>` — the
 * LifestylePill component doesn't care where the parent state stores them).
 *
 * Feb 2026 — Religion, Politics, Education, Zodiac were REMOVED from this
 * grid because they're already single-source-of-truth PillSelects in
 * StepAboutYou.jsx. Keeping them here caused conflicting state between the
 * two editors.
 */
export const LIFESTYLE_SECTIONS = [
  {
    title: 'Relationship',
    testId: 'lifestyle-group-relationship',
    fields: [
      { key: 'has_kids',   label: 'Have kids',  options: ['yes','no','prefer_not_say'], labelMap: { prefer_not_say: 'Prefer not to say' } },
      { key: 'wants_kids', label: 'Want kids',  options: ['yes','no','someday','not_sure'], labelMap: { not_sure: 'Not sure' } },
      { key: 'marital_history', label: 'Have you ever been married?',
        options: ['never_married','divorced','separated','widowed'],
        labelMap: { never_married: 'Never married' } },
      { key: 'monogamy', label: 'Relationship style',
        options: ['monogamous','non_monogamous','open'],
        labelMap: { non_monogamous: 'Non-monogamous', open: 'Open to either' } },
      { key: 'willing_to_relocate', label: 'Willing to relocate',
        options: ['local_only','open_to_ldr','open_to_relocating'],
        labelMap: {
          local_only: 'Local only',
          open_to_ldr: 'Open to long-distance',
          open_to_relocating: 'Open to relocating',
        } },
    ],
  },
  {
    title: 'Lifestyle',
    testId: 'lifestyle-group-lifestyle',
    fields: [
      // Feb 2026 · lifestyle terminology polish. Drinking loses "sometimes"
      // (overlapped with "socially" for alcohol). Smoking + marijuana share
      // the same 5-value scale ending in "formerly" (more polished than
      // "quit"). Recreational drugs added as a fourth substance-use field.
      // Sleep schedule added — small but meaningful compatibility signal.
      { key: 'drinking',  label: 'Drinking',  options: ['never','rarely','socially','often'] },
      { key: 'smoking',   label: 'Smoking',   options: ['never','socially','sometimes','often','formerly'] },
      { key: 'marijuana', label: 'Marijuana', options: ['never','socially','sometimes','often','formerly'] },
      { key: 'drugs',     label: 'Recreational drugs',
        options: ['never','socially','sometimes','prefer_not_say'],
        labelMap: { prefer_not_say: 'Prefer not to say' } },
      { key: 'exercise',  label: 'Exercise',  options: ['daily','often','sometimes','rarely','never'] },
      // Feb 2026 · NEW — activity level (how active you are day-to-day).
      // Distinct from exercise which measures frequency; activity_level
      // captures overall lifestyle intensity.
      { key: 'activity_level', label: 'Activity level',
        options: ['very_active','active','sometimes_active','not_very_active'],
        labelMap: {
          very_active: 'Very active',
          active: 'Active',
          sometimes_active: 'Sometimes active',
          not_very_active: 'Not very active',
        } },
      // Feb 2026 · NEW — ideal weekend (lifestyle rhythm signal). Note:
      // there is a separate `lifestyle` compatibility question with similar
      // framing but different options — kept both intentionally as the
      // Compatibility Q is a psychological signal while this is a
      // lifestyle-side factual answer.
      { key: 'ideal_weekend', label: 'Ideal weekend',
        options: ['going_out','staying_in','exploring','mix'],
        labelMap: {
          going_out: 'Going out',
          staying_in: 'Staying in',
          exploring: 'Exploring & adventures',
          mix: 'A mix of both',
        } },
      { key: 'sleep_schedule', label: 'Sleep schedule',
        options: ['early_bird','night_owl','somewhere_in_between'],
        labelMap: {
          early_bird: 'Early bird',
          night_owl: 'Night owl',
          somewhere_in_between: 'Somewhere in between',
        } },
    ],
  },
];

// Flattened list kept for backward-compat with any consumer still importing
// LIFESTYLE_FIELDS (e.g., admin tools). New code should use LIFESTYLE_SECTIONS.
export const LIFESTYLE_FIELDS = LIFESTYLE_SECTIONS.flatMap((s) => s.fields);

export function LifestylePill({ field, value, onChange }) {
  const displayLabel = (opt) => field.labelMap?.[opt] || opt.charAt(0).toUpperCase() + opt.slice(1);
  return (
    <div className="p-3.5 rounded-2xl border border-[var(--parity-hairline)] bg-white" data-testid={`lifestyle-${field.key}`}>
      <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--parity-ink-soft)] mb-2">
        {field.label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {field.options.map((opt) => {
          const active = value === opt;
          return (
            <button
              type="button"
              key={opt}
              onClick={() => onChange(active ? '' : opt)}
              data-testid={`lifestyle-${field.key}-${opt}`}
              className={
                'px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ' +
                (active
                  ? 'bg-[var(--parity-ink)] text-white border-[var(--parity-ink)]'
                  : 'bg-white text-[var(--parity-ink)] border-[var(--parity-hairline)] hover:bg-black/[0.02]')
              }
            >
              {displayLabel(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Shared input styles for text/number/textarea in setup steps. */
export const inputCls =
  'rounded-xl bg-black/[0.03] border-black/[0.08] text-white placeholder:text-zinc-500 focus-visible:ring-[#EF4444]/30 px-4 py-3';
